import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { MagicStoryCard } from "@/components/MagicStoryCard";
import { CreatureReveal } from "@/components/CreatureReveal";
import { StreakFlame } from "@/components/StreakFlame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Wand2, Send, Mic, MicOff, Share2, Loader2, ChevronDown, Flame } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

const CompanionPicker = ({ creatures, selected, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto pb-2" data-testid="companion-picker">
    {creatures.map((c) => {
      const active = selected === c.name;
      const img = c.image_path ? fileUrl(c.image_path) : c.image_url || null;
      return (
        <button
          key={c.key || c.id}
          onClick={() => onSelect(active ? null : c.name)}
          data-testid="companion-option"
          className={`flex shrink-0 items-center gap-1.5 rounded-full border py-1.5 text-sm transition-colors ${img ? "pl-1.5 pr-3" : "px-3"} ${
            active ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/10 text-[#A3B1C6] hover:border-[#D4AF37]/40"
          }`}
        >
          {img ? (
            <img src={img} alt={c.name} className="h-6 w-6 rounded-full object-cover" style={{ border: `1px solid ${c.color}66` }} />
          ) : (
            <Sparkles className="h-3.5 w-3.5" style={{ color: c.color }} />
          )}
          {c.name}
        </button>
      );
    })}
  </div>
);

export default function Chat() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [casting, setCasting] = useState(false);
  const [listening, setListening] = useState(false);
  const [creatures, setCreatures] = useState([]);
  const [companion, setCompanion] = useState(() => localStorage.getItem("companion") || null);
  const [showPicker, setShowPicker] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [streak, setStreak] = useState(0);
  const [sharedIds, setSharedIds] = useState({});
  const recogRef = useRef(null);
  const endRef = useRef(null);
  const didInitScroll = useRef(false);

  const fmtDate = (d) => {
    if (d === todayStr()) return t("chat.today");
    const [y, m, day] = d.split("-");
    return lang === "en"
      ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : `${y}年${parseInt(m)}月${parseInt(day)}日`;
  };

  useEffect(() => {
    if (!user?.user_id) return;
    Promise.all([
      api.get(`/ai/chat/chat_${user.user_id}`),
      api.get("/journal"),
      api.get("/stats"),
    ]).then(([msgsRes, jRes, sRes]) => {
      setStreak(sRes.data?.diary_streak?.current || 0);
      const byDate = {};
      msgsRes.data.forEach((m) => {
        const d = (m.created_at || "").slice(0, 10) || todayStr();
        (byDate[d] = byDate[d] || { msgs: [], card: null }).msgs.push({ role: m.role, content: m.content });
      });
      jRes.data.filter((j) => j.magic).forEach((j) => {
        const d = j.entry_date;
        byDate[d] = byDate[d] || { msgs: [], card: null };
        byDate[d].card = {
          magic: j.magic, creature: j.creature, lucky_creatures: j.lucky_creatures,
          journal_id: j.id, extracted: { mood: j.mood, mood_emoji: j.mood_emoji },
        };
      });
      const timeline = [];
      Object.keys(byDate).sort().forEach((d) => {
        timeline.push({ type: "date", date: d });
        byDate[d].msgs.forEach((m) => timeline.push({ type: "msg", ...m }));
        if (byDate[d].card) timeline.push({ type: "card", data: byDate[d].card });
      });
      setItems(timeline);
    });
    api.get("/creatures").then((r) => setCreatures([...(r.data.custom || []), ...(r.data.base || [])]));
  }, [user?.user_id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: didInitScroll.current ? "smooth" : "auto" });
    if (items.length) didInitScroll.current = true;
  }, [items, loading]);

  const pickCompanion = (name) => {
    setCompanion(name);
    if (name) localStorage.setItem("companion", name);
    else localStorage.removeItem("companion");
  };

  const appendToday = (entry) => {
    setItems((p) => {
      const lastDate = [...p].reverse().find((x) => x.type === "date");
      const next = lastDate?.date === todayStr() ? [...p] : [...p, { type: "date", date: todayStr() }];
      return [...next, entry];
    });
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input;
    setInput("");
    appendToday({ type: "msg", role: "user", content: msg });
    setLoading(true);
    try {
      const res = await api.post("/ai/chat", { message: msg, lang, companion });
      appendToday({ type: "msg", role: "assistant", content: res.data.reply });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("t.error"));
    } finally {
      setLoading(false);
    }
  };

  const cast = async () => {
    if (casting) return;
    setCasting(true);
    try {
      const res = await api.post("/ai/chat-diary", { lang, preferred_creature: companion });
      if (res.data?.creature?.name) {
        setReveal({ creature: res.data.creature, region: res.data.region });
      }
      appendToday({ type: "card", data: res.data, fresh: true });
      setStreak(res.data?.streak?.current || streak);
      toast.success(t("t.translate_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("t.error"));
    } finally {
      setCasting(false);
    }
  };

  const share = async (journalId) => {
    try {
      await api.post("/shares", { journal_id: journalId, show_country: user?.share_location !== false });
      setSharedIds((p) => ({ ...p, [journalId]: true }));
      toast.success(t("diary.shared"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("t.error"));
    }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error(t("t.voice_unsupported"));
    if (listening) {
      recogRef.current?.stop();
      setListening(false);
      return;
    }
    const recog = new SR();
    recog.lang = lang === "en" ? "en-US" : "zh-TW";
    recog.onresult = (ev) => {
      const text = Array.from(ev.results).map((r) => r[0].transcript).join("");
      setInput((p) => (p ? p + " " : "") + text);
    };
    recog.onend = () => setListening(false);
    recog.start();
    recogRef.current = recog;
    setListening(true);
  };

  return (
    <>
      <AnimatePresence>
        {reveal && (
          <CreatureReveal creature={reveal.creature} region={reveal.region} onClose={() => setReveal(null)} />
        )}
      </AnimatePresence>

      <div className="mx-auto flex h-[calc(100vh-150px)] max-w-3xl flex-col md:h-[calc(100vh-120px)]" data-testid="chat-page">
        {/* companion + streak bar */}
        <div className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowPicker((p) => !p)}
              data-testid="companion-toggle"
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#0A0F1D]/80 px-4 py-2 text-sm text-[#A3B1C6] hover:border-[#D4AF37]/50"
            >
              <Sparkles className="h-4 w-4 text-[#D4AF37]" />
              {t("chat.companion_label")}：
              <span className="text-[#D4AF37]">{companion || t("chat.companion_none")}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showPicker ? "rotate-180" : ""}`} />
            </button>
            {streak > 0 && (
              <span
                data-testid="streak-chip"
                className="flex items-center gap-1.5 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-2 text-sm text-orange-300"
              >
                <Flame className="h-4 w-4 text-orange-400" />
                {t("chat.streak", { n: streak })}
              </span>
            )}
          </div>
          <AnimatePresence>
            {showPicker && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 overflow-hidden"
              >
                <CompanionPicker creatures={creatures} selected={companion} onSelect={pickCompanion} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* timeline */}
        <div className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-white/5 bg-[#0A0F1D]/60 p-4 backdrop-blur-xl md:p-6" data-testid="chat-messages">
          {items.length === 0 && (
            <div className="flex items-start gap-2">
              <Sparkles className="mt-1 h-4 w-4 shrink-0 text-[#9D4CDD]" />
              <p className="max-w-[85%] rounded-2xl rounded-tl-none bg-[#9D4CDD]/10 p-3 text-sm leading-relaxed text-[#FDFBF7]">
                {t("chat.greeting")}
              </p>
            </div>
          )}
          {items.map((it, i) => {
            if (it.type === "date") {
              return (
                <div key={i} className="flex items-center gap-3 py-1" data-testid="date-separator">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="rounded-full border border-white/10 bg-[#050814] px-3 py-1 text-xs tracking-widest text-[#A3B1C6]">
                    {fmtDate(it.date)}
                  </span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
              );
            }
            if (it.type === "card") {
              const d = it.data;
              return (
                <div key={i} className="py-2">
                  {it.fresh && d.streak && <div className="mb-3"><StreakFlame streak={d.streak} /></div>}
                  <MagicStoryCard
                    magic={d.magic}
                    creature={d.creature}
                    luckyCreatures={d.lucky_creatures}
                    region={d.region}
                    mood={d.extracted?.mood}
                    moodEmoji={d.extracted?.mood_emoji}
                    footer={
                      <Button
                        onClick={() => share(d.journal_id)}
                        disabled={!!sharedIds[d.journal_id]}
                        data-testid="chat-share-button"
                        className="w-full rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20"
                      >
                        {sharedIds[d.journal_id] ? t("diary.shared") : <><Share2 className="mr-2 h-4 w-4" /> {t("diary.share")}</>}
                      </Button>
                    }
                  />
                </div>
              );
            }
            return (
              <div key={i} className={`flex ${it.role === "user" ? "justify-end" : "items-start gap-2"}`}>
                {it.role === "assistant" && <Sparkles className="mt-1 h-4 w-4 shrink-0 text-[#9D4CDD]" />}
                <p
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl p-3 text-sm leading-relaxed ${
                    it.role === "user"
                      ? "rounded-br-none bg-[#D4AF37]/15 text-[#FDFBF7]"
                      : "rounded-tl-none bg-[#9D4CDD]/10 text-[#FDFBF7]"
                  }`}
                >
                  {it.content}
                </p>
              </div>
            );
          })}
          {loading && <Loader2 className="h-4 w-4 animate-spin text-[#9D4CDD]" />}
          <div ref={endRef} />
        </div>

        {/* cast + input bar */}
        <div className="mt-3 space-y-2">
          <Button
            onClick={cast}
            disabled={casting}
            data-testid="cast-magic-button"
            className="w-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#f0d787] py-5 text-sm font-medium text-[#050814] hover:opacity-90"
          >
            {casting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("chat.casting")}</>
            ) : (
              <><Wand2 className="mr-2 h-4 w-4" /> {t("chat.cast")}</>
            )}
          </Button>
          <div className="flex gap-2">
            <button
              onClick={toggleVoice}
              data-testid="chat-voice-button"
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
                listening ? "border-red-400 text-red-400" : "border-white/10 text-[#A3B1C6] hover:border-[#D4AF37]/40"
              }`}
            >
              {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("chat.ph")}
              data-testid="chat-input"
              className="border-white/10 bg-[#0A0F1D] focus-visible:ring-[#D4AF37]"
            />
            <Button onClick={send} disabled={loading} data-testid="chat-send" className="shrink-0 rounded-full bg-[#9D4CDD] hover:opacity-90">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
